import { ChevronLeft, ChevronRight } from 'lucide-react';
import { orgMonth, orgToday } from '@/lib/today';
import * as React from 'react';
import { formatDuration, startOfWeek, weekDays } from '@cfl/shared';

import {
  Alert, AlertDescription, Badge, Button, Card, CardContent, Skeleton,
  Tooltip, TooltipContent, TooltipTrigger,
} from '@/components/cargofl-ui';
import { PageContainer } from '@/components/shell/page-container';
import { PageHeader } from '@/components/shell/page-header';
import { useMonth, type MonthDay } from '@/lib/working-time-api';

/**
 * The month as a calendar.
 *
 * The same data as the working-time table, laid out the way people hold a month in
 * their heads - so a gap is a hole you can see rather than a row you have to find.
 * That is the whole reason it exists alongside the table, and why it reuses the
 * month endpoint rather than querying differently: two views of one month must
 * never disagree.
 */
export function CalendarPage() {
  const [month, setMonth] = React.useState(() => orgMonth());
  const { data, isPending, isError, error } = useMonth(month);
  const today = orgToday();

  // Padded to whole weeks, Monday first, so the columns line up with the weekday
  // headings and the grid has no ragged first row.
  const cells = React.useMemo(() => {
    if (!data?.days.length) return [];
    const byDate = new Map(data.days.map((d) => [d.day, d]));
    const first = data.days[0]!.day;
    const last = data.days[data.days.length - 1]!.day;
    const out: { date: string; day: MonthDay | undefined; inMonth: boolean }[] = [];

    let cursor = startOfWeek(first);
    while (cursor <= last || out.length % 7 !== 0) {
      out.push({ date: cursor, day: byDate.get(cursor), inMonth: cursor.slice(0, 7) === month });
      const next = new Date(`${cursor}T00:00:00.000Z`);
      next.setUTCDate(next.getUTCDate() + 1);
      cursor = next.toISOString().slice(0, 10);
      // Guard against a runaway loop if the month data is ever malformed.
      if (out.length > 42) break;
    }
    return out;
  }, [data, month]);

  return (
    <PageContainer>
      <PageHeader
        title="Calendar"
        description="Your month at a glance — hours recorded, holidays and leave."
        actions={
          <div className="flex items-center gap-1">
            <Button variant="outline" size="icon" aria-label="Previous month"
              onClick={() => setMonth(shift(month, -1))}>
              <ChevronLeft className="size-4" />
            </Button>
            <Button hint={"Jump to the current month"} variant="outline" onClick={() => setMonth(orgMonth())}>
              This month
            </Button>
            <Button variant="outline" size="icon" aria-label="Next month"
              onClick={() => setMonth(shift(month, 1))}>
              <ChevronRight className="size-4" />
            </Button>
          </div>
        }
      />

      {isError && (
        <Alert className="border-destructive/30">
          <AlertDescription className="text-sm">
            {error instanceof Error ? error.message : 'Could not load that month.'}
          </AlertDescription>
        </Alert>
      )}

      {isPending && <Skeleton className="h-96 w-full" />}

      {data && !data.contract && (
        <Alert>
          <AlertDescription className="text-sm leading-relaxed">
            {data.message ??
              'No working contract is set up for you yet, so there is no target to compare against.'}
          </AlertDescription>
        </Alert>
      )}

      {data?.totals && (
        <div className="flex flex-wrap items-baseline justify-between gap-3">
          <p className="text-sm font-semibold">{monthLabel(month)}</p>
          <p className="flex flex-wrap gap-x-6 text-sm">
            <span className="text-muted-foreground">
              Target <span className="ts-num font-medium text-foreground">
                {formatDuration(data.totals.expectedSeconds)}
              </span>
            </span>
            <span className="text-muted-foreground">
              Worked <span className="ts-num font-medium text-foreground">
                {formatDuration(data.totals.actualSeconds)}
              </span>
            </span>
            <span
              className={`ts-num font-semibold ${
                data.totals.balanceSeconds < 0 ? 'text-destructive' : 'text-primary'
              }`}
            >
              {signed(data.totals.balanceSeconds)}
            </span>
          </p>
        </div>
      )}

      {cells.length > 0 && (
        <Card>
          <CardContent className="p-3">
            <div className="grid grid-cols-7 gap-1.5">
              {weekDays('2026-08-31').map((d) => (
                <div
                  key={d}
                  className="pb-1 text-center text-[11px] font-semibold uppercase tracking-wider text-muted-foreground"
                >
                  {new Date(`${d}T00:00:00.000Z`).toLocaleDateString('en-IN', {
                    weekday: 'short',
                    timeZone: 'UTC',
                  })}
                </div>
              ))}

              {cells.map((cell) => (
                <DayCell key={cell.date} cell={cell} isToday={cell.date === today} />
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <div className="flex flex-wrap gap-4 text-xs text-muted-foreground">
        <Legend swatch="bg-primary/15 border-primary/40" label="Met or exceeded the target" />
        <Legend swatch="bg-destructive/10 border-destructive/40" label="Short of the target" />
        <Legend swatch="bg-muted border-border" label="Nothing expected" />
      </div>
    </PageContainer>
  );
}

function DayCell({
  cell,
  isToday,
}: {
  cell: { date: string; day: MonthDay | undefined; inMonth: boolean };
  isToday: boolean;
}) {
  const { day, inMonth, date } = cell;

  // Days padding the grid from the neighbouring month are shown faintly rather than
  // left blank: an empty square reads as "no data" for a day that has plenty.
  if (!day || !inMonth) {
    return (
      <div className="min-h-[84px] rounded-md border border-dashed border-border/50 p-2">
        <div className="ts-num text-xs text-muted-foreground/40">{Number(date.slice(8, 10))}</div>
      </div>
    );
  }

  const expected = day.expectedSeconds;
  const actual = day.actualSeconds;
  const short = expected > 0 && actual < expected;
  const met = expected > 0 && actual >= expected;

  const tone = met
    ? 'bg-primary/[0.07] border-primary/30'
    : short
      ? 'bg-destructive/[0.06] border-destructive/30'
      : 'bg-muted/40 border-border';

  const cellBody = (
    <div
      className={`min-h-[84px] rounded-md border p-2 ${tone} ${
        isToday ? 'ring-2 ring-primary ring-offset-1' : ''
      }`}
    >
      <div className="flex items-baseline justify-between">
        <span className="ts-num text-xs font-medium">{Number(date.slice(8, 10))}</span>
        {day.holidayName && <Badge variant="outline" className="px-1 py-0 text-[9px]">hol</Badge>}
        {!day.holidayName && day.note?.includes('absence') && (
          <Badge variant="secondary" className="px-1 py-0 text-[9px]">leave</Badge>
        )}
      </div>

      <div className={`ts-num mt-1.5 text-sm ${actual ? 'font-semibold' : 'text-muted-foreground'}`}>
        {actual ? formatDuration(actual) : '—'}
      </div>
      {expected > 0 && (
        <div className="ts-num text-[11px] text-muted-foreground">
          of {formatDuration(expected)}
        </div>
      )}
    </div>
  );

  // Only days with something to explain get a tooltip; a tooltip on every square
  // that just repeats the number on it is noise.
  if (!day.holidayName && !day.note) return cellBody;

  return (
    <Tooltip>
      <TooltipTrigger asChild>{cellBody}</TooltipTrigger>
      <TooltipContent>
        {day.holidayName ?? day.note}
        {expected === 0 && ' — nothing expected'}
      </TooltipContent>
    </Tooltip>
  );
}

function Legend({ swatch, label }: { swatch: string; label: string }) {
  return (
    <span className="flex items-center gap-1.5">
      <span className={`inline-block size-3 rounded border ${swatch}`} />
      {label}
    </span>
  );
}

function signed(seconds: number): string {
  if (seconds === 0) return '0:00';
  return `${seconds < 0 ? '−' : '+'}${formatDuration(Math.abs(seconds))}`;
}

function monthLabel(month: string): string {
  return new Date(`${month}-01T00:00:00.000Z`).toLocaleDateString('en-IN', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

function shift(month: string, delta: number): string {
  const [y, m] = month.split('-').map(Number);
  return new Date(Date.UTC(y!, m! - 1 + delta, 1)).toISOString().slice(0, 7);
}
